import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  StickyNote,
  Play,
  Pause,
  Home,
  SkipForward,
} from 'lucide-react';
import { PresentationData, ThemePreset, TypographySettings, AnimationSettings } from '../../types';
import SlideLayoutRenderer from './SlideLayoutRenderer';

interface FullscreenPresenterProps {
  presentation: PresentationData;
  theme: ThemePreset;
  typography: TypographySettings;
  animations: AnimationSettings;
  initialSlide?: number;
  onClose: () => void;
}

// Animation helper functions
const getSlideTransitionInitial = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 1 };
    case 'fade': return { opacity: 0 };
    case 'slide': return { opacity: 0, x: 100 };
    case 'zoom': return { opacity: 0, scale: 0.8 };
    case 'flip': return { opacity: 0, rotateY: -90 };
    case 'cube': return { opacity: 0, rotateY: -90, z: -200 };
    case 'dissolve': return { opacity: 0, filter: 'blur(10px)' };
    default: return { opacity: 0 };
  }
};

const getSlideTransitionAnimate = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 1 };
    case 'fade': return { opacity: 1 };
    case 'slide': return { opacity: 1, x: 0 };
    case 'zoom': return { opacity: 1, scale: 1 };
    case 'flip': return { opacity: 1, rotateY: 0 };
    case 'cube': return { opacity: 1, rotateY: 0, z: 0 };
    case 'dissolve': return { opacity: 1, filter: 'blur(0px)' };
    default: return { opacity: 1 };
  }
};

const getSlideTransitionExit = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 0 };
    case 'fade': return { opacity: 0 };
    case 'slide': return { opacity: 0, x: -100 };
    case 'zoom': return { opacity: 0, scale: 1.2 };
    case 'flip': return { opacity: 0, rotateY: 90 };
    case 'cube': return { opacity: 0, rotateY: 90, z: -200 };
    case 'dissolve': return { opacity: 0, filter: 'blur(10px)' };
    default: return { opacity: 0 };
  }
};

const getTextAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '0%' };
    case 'fade-in-up': return { opacity: 0, y: 30 };
    case 'fade-in-down': return { opacity: 0, y: -30 };
    case 'bounce-in': return { opacity: 0, scale: 0.3 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    default: return { opacity: 1 };
  }
};

const getTextAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '100%' };
    case 'fade-in-up': return { opacity: 1, y: 0 };
    case 'fade-in-down': return { opacity: 1, y: 0 };
    case 'bounce-in': return { opacity: 1, scale: 1 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 0 };
    case 'slide-up': return { opacity: 0, y: 30 };
    case 'slide-left': return { opacity: 0, x: 50 };
    case 'slide-right': return { opacity: 0, x: -50 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    case 'bounce': return { opacity: 0, y: 30 };
    case 'flip': return { opacity: 0, rotateX: -90 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 1 };
    case 'slide-up': return { opacity: 1, y: 0 };
    case 'slide-left': return { opacity: 1, x: 0 };
    case 'slide-right': return { opacity: 1, x: 0 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    case 'bounce': return { opacity: 1, y: 0 };
    case 'flip': return { opacity: 1, rotateX: 0 };
    default: return { opacity: 1 };
  }
};

export default function FullscreenPresenter({
  presentation,
  theme,
  typography,
  animations,
  initialSlide = 0,
  onClose,
}: FullscreenPresenterProps) {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Touch handling
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const totalSlides = presentation.slides?.length || 0;
  const slide = presentation.slides?.[currentSlide];

  // Navigation
  const goToSlide = useCallback((index: number) => {
    const clampedIndex = Math.max(0, Math.min(totalSlides - 1, index));
    setCurrentSlide(clampedIndex);
  }, [totalSlides]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const goToFirst = useCallback(() => setCurrentSlide(0), []);
  const goToLast = useCallback(() => setCurrentSlide(totalSlides - 1), [totalSlides]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Failed to enter fullscreen:', err);
      }
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Auto-play
  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlaying(prev => !prev);
  }, []);

  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayTimerRef.current = setInterval(() => {
        setCurrentSlide(prev => {
          if (prev >= totalSlides - 1) {
            setIsAutoPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, (animations.autoAdvanceDelay || 5) * 1000);
    } else {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
        autoPlayTimerRef.current = null;
      }
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, [isAutoPlaying, animations.autoAdvanceDelay, totalSlides]);

  // Hide controls after inactivity
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    hideControlsTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      resetControlsTimer();

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
        case 'PageDown':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          prevSlide();
          break;
        case 'Home':
          e.preventDefault();
          goToFirst();
          break;
        case 'End':
          e.preventDefault();
          goToLast();
          break;
        case 'Escape':
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            onClose();
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          setShowNotes(prev => !prev);
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          toggleAutoPlay();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToFirst, goToLast, onClose, toggleFullscreen, toggleAutoPlay, resetControlsTimer]);

  // Touch/swipe handling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      resetControlsTimer();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      // Only register horizontal swipes (ignore vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nextSlide, prevSlide, resetControlsTimer]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Mouse movement shows controls
  useEffect(() => {
    const handleMouseMove = () => resetControlsTimer();
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [resetControlsTimer]);

  // Initial controls timer
  useEffect(() => {
    resetControlsTimer();
    return () => {
      if (hideControlsTimerRef.current) {
        clearTimeout(hideControlsTimerRef.current);
      }
    };
  }, [resetControlsTimer]);

  const transitionDuration = animations.slideTransitionDuration / 1000;

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: theme.colors.background }}
      onClick={resetControlsTimer}
    >
      {/* Top Bar */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-0 left-0 right-0 h-16 bg-black/40 backdrop-blur-sm flex items-center justify-between px-6 z-10"
          >
            <div className="flex items-center gap-4">
              <h2 className="text-white font-semibold truncate max-w-md">
                {presentation.title}
              </h2>
              <span className="text-white/60 text-sm">
                {currentSlide + 1} / {totalSlides}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleAutoPlay}
                className={`p-2 rounded-lg transition-colors ${
                  isAutoPlaying ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                title={isAutoPlaying ? 'Pause (P)' : 'Auto-play (P)'}
              >
                {isAutoPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>

              <button
                onClick={() => setShowNotes(prev => !prev)}
                className={`p-2 rounded-lg transition-colors ${
                  showNotes ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                title="Toggle Notes (N)"
              >
                <StickyNote className="w-5 h-5" />
              </button>

              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>

              <button
                onClick={onClose}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors ml-2"
                title="Close (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Slide Area */}
      <div
        className="flex-1 flex items-center justify-center p-8 overflow-hidden"
        style={{ perspective: '1000px' }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={getSlideTransitionInitial(animations.slideTransition)}
            animate={getSlideTransitionAnimate(animations.slideTransition)}
            exit={getSlideTransitionExit(animations.slideTransition)}
            transition={{ duration: transitionDuration, ease: 'easeInOut' }}
            className="w-full h-full max-w-7xl max-h-full rounded-2xl overflow-hidden"
            style={{
              transformStyle: 'preserve-3d',
              background: slide?.backgroundColor || theme.colors.background,
            }}
          >
            {slide ? (
              <SlideLayoutRenderer
                slide={slide}
                theme={theme}
                typography={typography}
                animations={animations}
                isFullscreen={true}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <p style={{ color: theme.colors.text, opacity: 0.5 }}>No slide content</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Presenter Notes Panel */}
      <AnimatePresence>
        {showNotes && slide?.notes && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-24 left-8 right-8 max-h-48 bg-black/70 backdrop-blur-md rounded-xl p-4 overflow-y-auto z-10"
          >
            <h4 className="text-white/70 text-sm font-medium mb-2">Presenter Notes</h4>
            <p className="text-white text-sm whitespace-pre-wrap">{slide.notes}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Controls */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm z-10"
          >
            {/* Progress Bar */}
            <div className="h-1 bg-white/20">
              <motion.div
                className="h-full"
                style={{ background: theme.colors.primary }}
                initial={{ width: 0 }}
                animate={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Navigation Controls */}
            <div className="h-16 flex items-center justify-center gap-4 px-6">
              <button
                onClick={goToFirst}
                disabled={currentSlide === 0}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="First Slide (Home)"
              >
                <Home className="w-5 h-5" />
              </button>

              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className="p-3 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous Slide (Left Arrow)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Slide Thumbnails */}
              <div className="flex items-center gap-2 px-4">
                {presentation.slides?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide
                        ? 'scale-125'
                        : 'opacity-50 hover:opacity-80'
                    }`}
                    style={{
                      background: index === currentSlide ? theme.colors.primary : 'white',
                    }}
                    title={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                disabled={currentSlide === totalSlides - 1}
                className="p-3 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next Slide (Right Arrow)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              <button
                onClick={goToLast}
                disabled={currentSlide === totalSlides - 1}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Last Slide (End)"
              >
                <SkipForward className="w-5 h-5" />
              </button>
            </div>

            {/* Keyboard Shortcuts Hint */}
            <div className="absolute bottom-4 right-6 text-white/40 text-xs hidden md:block">
              Space/Arrow: Navigate | F: Fullscreen | N: Notes | Esc: Exit
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click Areas for Navigation */}
      <div
        className="absolute left-0 top-16 bottom-16 w-1/4 cursor-pointer z-5"
        onClick={prevSlide}
        title="Previous Slide"
      />
      <div
        className="absolute right-0 top-16 bottom-16 w-1/4 cursor-pointer z-5"
        onClick={nextSlide}
        title="Next Slide"
      />
    </motion.div>
  );
}
